import React from 'react';

import { Pagesection } from '@ktag/styleguide/dist/react';
import { Issue } from 'api/api.types';
import { useTranslation } from 'react-i18next';
import ElectionTeaser from 'lib/PollTeaser/ElectionTeaser';
import PollTeaser from 'lib/PollTeaser/PollTeaser';
import ProgressBar from 'lib/ProgressBar/ProgressBar';
import classNames from 'classnames';

interface Props {
  issues: Issue[];
  type: 'POLL' | 'ELECTION';
  headingLevel?: 2 | 3;
}

const PollTeaserList = ({ issues, type, headingLevel }: Props) => {
  const { t } = useTranslation('wap');
  let label = type === 'POLL' ? t('polls') : t('elections');

  // as all polls are counted together and share one progress bar, we only need to get the numbers for any of the polls
  const totalCountingCircles = issues[0]?.progress?.total_number_of_counting_circles;
  const finishedCountingCircles =
    issues[0]?.progress?.number_of_finished_counting_circles;

  const hasCountStarted =
    finishedCountingCircles > 0 && finishedCountingCircles < totalCountingCircles;

  const hasCountFinished = issues?.every((issue) => issue?.progress?.final);

  if (hasCountStarted && type === 'POLL') label += ` – ${t('result_ongoing_plural')}`;
  if (hasCountFinished) label += ` – ${t('result_final_plural')}`;

  const HeadingTag = `h${headingLevel}` as 'h2' | 'h3';

  return (
    <>
      <Pagesection
        additionalClassNames={classNames({ pollteaserlist__polls: type === 'POLL' })}
      >
        <HeadingTag className="pollteaserlist__heading">{label}</HeadingTag>
        {type === 'POLL' && <ProgressBar issue={issues[0]} />}
      </Pagesection>
      <Pagesection type="medium">
        <div className="pollteaserlist__container">
          <ol className="pollteaserlist__list">
            {type === 'POLL' &&
              issues.map((poll) => (
                <li key={poll.info.id} className="pollteaserlist__item">
                  <PollTeaser poll={poll} headingLevel={(headingLevel + 1) as 3 | 4} />
                </li>
              ))}
            {type === 'ELECTION' &&
              issues.map((election) => (
                <li key={election.info.id} className="pollteaserlist__item">
                  <ElectionTeaser
                    election={election}
                    isFinal={election.progress.final}
                    key={election.info.id}
                    headingLevel={(headingLevel + 1) as 3 | 4}
                  />
                </li>
              ))}
          </ol>
        </div>
      </Pagesection>
    </>
  );
};

PollTeaserList.defaultProps = {
  headingLevel: 2,
};

export default PollTeaserList;
